import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  AreaChart, Area, Legend
} from 'recharts';
import SafeResponsiveContainer from './ui/SafeResponsiveContainer';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, TrendingDown, Minus, Activity, 
  Brain, Smile, Zap, Calendar, ChevronDown
} from 'lucide-react';

import { API } from '../config/api';

export default function JournalTrends({ childId, childName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);
  const [viewMode, setViewMode] = useState('all'); // all, rpe, performance, mood

  const fetchTrends = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/session-journal/trends/${childId}?days=${timeRange}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch journal trends');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [childId, timeRange]);

  useEffect(() => {
    if (childId) {
      fetchTrends();
    }
  }, [childId, fetchTrends]);

  const TrendIndicator = ({ value, label, positive = true }) => {
    if (value === null || value === undefined) return null;
    
    const isUp = value > 0;
    const isNeutral = Math.abs(value) < 0.3;
    
    let color = isNeutral ? 'text-zinc-500' : (isUp === positive ? 'text-green-500' : 'text-[#ff025f]');
    let Icon = isNeutral ? Minus : (isUp ? TrendingUp : TrendingDown);
    
    return (
      <div className="flex items-center gap-1">
        <Icon className={`h-3 w-3 ${color}`} />
        <span className={`text-xs ${color}`}>
          {value > 0 ? '+' : ''}{value}
        </span>
      </div>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-xl">
        <p className="text-zinc-400 text-xs mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-zinc-300">{entry.name}:</span>
            <span className="font-semibold" style={{ color: entry.color }}>
              {entry.value?.toFixed(1) || '-'} / 10
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 animate-pulse">
        <div className="h-6 bg-zinc-800 rounded w-48 mb-4" />
        <div className="h-64 bg-zinc-800 rounded" />
      </div>
    );
  }

  if (!data || !data.chart_data || data.chart_data.length === 0) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6" data-testid="journal-trends-empty">
        <div className="flex items-center gap-3 mb-4">
          <Activity className="h-5 w-5 text-[#02b5ff]" />
          <h3 className="font-bold text-white">Session Journal Trends</h3>
        </div>
        <div className="text-center py-8">
          <Calendar className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No journal entries yet</p>
          <p className="text-xs text-zinc-600 mt-1">
            Start logging sessions in the AI Coach tab to see trends here
          </p>
        </div>
      </div>
    );
  }

  const { chart_data, summary } = data;

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="journal-trends">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Activity className="h-5 w-5 text-[#02b5ff]" />
            <h3 className="font-bold text-white">Session Journal Trends</h3>
          </div>
          
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(parseInt(e.target.value))}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-zinc-800/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1">
                <Zap className="h-4 w-4 text-yellow-500" />
                <span className="text-xs text-zinc-400">Avg RPE</span>
              </div>
              <TrendIndicator value={summary.rpe_trend} positive={false} />
            </div>
            <div className="text-xl font-bold text-yellow-500">
              {summary.avg_rpe?.toFixed(1) || '-'}
              <span className="text-xs text-zinc-500 font-normal"> / 10</span>
            </div>
          </div>

          <div className="bg-zinc-800/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1">
                <TrendingUp className="h-4 w-4 text-[#02b5ff]" />
                <span className="text-xs text-zinc-400">Avg Performance</span>
              </div>
              <TrendIndicator value={summary.performance_trend} positive={true} />
            </div>
            <div className="text-xl font-bold text-[#02b5ff]">
              {summary.avg_performance?.toFixed(1) || '-'}
              <span className="text-xs text-zinc-500 font-normal"> / 10</span>
            </div>
          </div>

          <div className="bg-zinc-800/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1">
                <Smile className="h-4 w-4 text-green-500" />
                <span className="text-xs text-zinc-400">Avg Mood</span>
              </div>
              <TrendIndicator value={summary.mood_trend} positive={true} />
            </div>
            <div className="text-xl font-bold text-green-500">
              {summary.avg_mood?.toFixed(1) || '-'}
              <span className="text-xs text-zinc-500 font-normal"> / 10</span>
            </div>
          </div>

          <div className="bg-zinc-800/50 rounded-lg p-3">
            <div className="flex items-center gap-1 mb-1">
              <Calendar className="h-4 w-4 text-purple-500" />
              <span className="text-xs text-zinc-400">Sessions</span>
            </div>
            <div className="text-xl font-bold text-purple-500">
              {summary.total_sessions}
            </div>
            <div className="text-[10px] text-zinc-500">
              {summary.training_sessions} training • {summary.matches} matches
            </div>
          </div>
        </div>

        {/* Match Record */}
        {summary.matches > 0 && (
          <div className="mt-3 flex items-center gap-4 text-sm">
            <span className="text-zinc-500">Match Record:</span>
            <span className="text-green-500 font-medium">{summary.match_wins}W</span>
            <span className="text-zinc-500">-</span>
            <span className="text-red-500 font-medium">{summary.match_losses}L</span>
          </div>
        )}
      </div>

      {/* View Mode Toggles */}
      <div className="px-4 pt-4 flex gap-2">
        <Button
          size="sm"
          variant={viewMode === 'all' ? 'default' : 'outline'}
          onClick={() => setViewMode('all')}
          className="text-xs"
        >
          All Metrics
        </Button>
        <Button
          size="sm"
          variant={viewMode === 'rpe' ? 'default' : 'outline'}
          onClick={() => setViewMode('rpe')}
          className="text-xs"
        >
          <Zap className="h-3 w-3 mr-1" /> Effort
        </Button>
        <Button
          size="sm"
          variant={viewMode === 'performance' ? 'default' : 'outline'}
          onClick={() => setViewMode('performance')}
          className="text-xs"
        >
          <TrendingUp className="h-3 w-3 mr-1" /> Performance
        </Button>
        <Button
          size="sm"
          variant={viewMode === 'mood' ? 'default' : 'outline'}
          onClick={() => setViewMode('mood')}
          className="text-xs"
        >
          <Smile className="h-3 w-3 mr-1" /> Mood
        </Button>
      </div>

      {/* Chart */}
      <div className="p-4">
        <SafeResponsiveContainer width="100%" height={280} minHeight={280}>
          {viewMode === 'all' ? (
            <LineChart data={chart_data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis 
                dataKey="date" 
                stroke="#666" 
                tick={{ fontSize: 10 }}
                tickFormatter={(val) => {
                  const d = new Date(val);
                  return `${d.getDate()}/${d.getMonth() + 1}`;
                }}
              />
              <YAxis domain={[0, 10]} stroke="#666" tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="rpe" 
                name="Effort (RPE)" 
                stroke="#EAB308" 
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
              <Line 
                type="monotone" 
                dataKey="performance" 
                name="Performance" 
                stroke="#02b5ff" 
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
              <Line 
                type="monotone" 
                dataKey="mood_after" 
                name="Mood" 
                stroke="#22C55E" 
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
            </LineChart>
          ) : (
            <AreaChart data={chart_data}>
              <defs>
                <linearGradient id="rpeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#EAB308" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#EAB308" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="perfGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#02b5ff" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#02b5ff" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="moodGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22C55E" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22C55E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis 
                dataKey="date" 
                stroke="#666" 
                tick={{ fontSize: 10 }}
                tickFormatter={(val) => {
                  const d = new Date(val);
                  return `${d.getDate()}/${d.getMonth() + 1}`;
                }}
              />
              <YAxis domain={[0, 10]} stroke="#666" tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              
              {viewMode === 'rpe' && (
                <>
                  <Area
                    type="monotone"
                    dataKey="rpe"
                    name="Effort (RPE)"
                    stroke="#EAB308"
                    fill="url(#rpeGradient)"
                    strokeWidth={2}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="rpe_avg_7d"
                    name="7-Day Avg"
                    stroke="#EAB308"
                    strokeDasharray="5 5"
                    strokeWidth={1}
                    dot={false}
                    connectNulls
                  />
                </>
              )}
              
              {viewMode === 'performance' && (
                <>
                  <Area
                    type="monotone"
                    dataKey="performance"
                    name="Performance"
                    stroke="#02b5ff"
                    fill="url(#perfGradient)"
                    strokeWidth={2}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="performance_avg_7d"
                    name="7-Day Avg"
                    stroke="#02b5ff"
                    strokeDasharray="5 5"
                    strokeWidth={1}
                    dot={false}
                    connectNulls
                  />
                </>
              )}
              
              {viewMode === 'mood' && (
                <>
                  <Area
                    type="monotone"
                    dataKey="mood_after"
                    name="Mood"
                    stroke="#22C55E"
                    fill="url(#moodGradient)"
                    strokeWidth={2}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="mood_avg_7d"
                    name="7-Day Avg"
                    stroke="#22C55E"
                    strokeDasharray="5 5"
                    strokeWidth={1}
                    dot={false}
                    connectNulls
                  />
                </>
              )}
            </AreaChart>
          )}
        </SafeResponsiveContainer>
      </div>

      {/* Interpretation */}
      <div className="px-4 pb-4">
        <div className="bg-zinc-800/30 rounded-lg p-3 text-xs text-zinc-400">
          <p className="mb-1">
            <strong className="text-zinc-300">💡 Understanding the data:</strong>
          </p>
          <ul className="space-y-1 list-disc list-inside">
            <li><span className="text-yellow-500">RPE (Rate of Perceived Exertion)</span>: How hard the session felt (lower = easier recovery needed)</li>
            <li><span className="text-[#02b5ff]">Performance</span>: Self-rated performance during session (higher = better)</li>
            <li><span className="text-green-500">Mood</span>: Emotional state after session (higher = more positive)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
